// SQL Server: does this connection tell `Sales` from `sales`, and `dbo.V` from
// `dbo.v`? Two DIFFERENT collations answer those, so this module runs two
// probes.
//
//   - DATABASE names are compared in the SERVER (instance) collation, not any
//     database's own — so ONE probe per connection answers it for every
//     database the connection spans. (That holds for a contained database too:
//     its row lives in `master`, and "identifiers of instance-level objects,
//     such as logins and database names, are assigned the default collation of
//     the instance" — COLLATE (Transact-SQL).)
//   - OBJECT and SCHEMA names are compared in the CATALOG collation of the
//     DATABASE that holds them. One instance can host a `_CI_` database next
//     to a `_CS_` one, so that probe runs once per database IDENTITY and must
//     run INSIDE that database (through `fetchRows(connectionId, database, …)`,
//     so `''` resolves to the login's default database — which is exactly the
//     entry that indexes).
//
// The CATALOG collation is not always the database's own (data) collation, and
// reading `DATABASEPROPERTYEX(DB_NAME(), 'Collation')` for it was wrong twice:
//
//   - in a PARTIALLY CONTAINED database (`CONTAINMENT = PARTIAL`) metadata is
//     collated in a fixed `Latin1_General_100_CI_AS_WS_KS_SC` — "the same for
//     all contained databases on all instances of SQL Server", and it "can't be
//     changed" — while the data collation stays whatever the user chose, so a
//     `_CS_` contained database still has ONE `dbo.users`;
//   - an Azure SQL Database sets its catalog collation at creation
//     (`CATALOG_COLLATION = DATABASE_DEFAULT | SQL_Latin1_General_CP1_CI_AS`),
//     independently of `COLLATE`.
//
// Both cases keyed `dbo.users` and `dbo.Users` apart in a database where they
// are one object, which dropped valid references from the References tab and
// from cursor resolution. See `Docs/IntelliSenseSnapshotLoad.md` → "Case policy
// first", and https://learn.microsoft.com/en-us/sql/relational-databases/databases/contained-database-collations
//
// Most instances and databases ship a `_CI_` collation, which is why the
// navigational index folds both kinds of key by default. A `_CS_` (or binary)
// collation genuinely has two `Sales` databases / two `dbo.V` views, and
// folding them serves one's index, References tab and DDL invalidation for the
// other — a successful `DROP VIEW IF EXISTS dbo.v` then erased `dbo.V`'s
// references.
//
// Both answers are remembered per connection in `casePolicyState.ts` (one
// generation guard, one teardown rule, shared with the MySQL probe).
// `undefined` means "not asked yet, or the probe failed" — the lifecycle's
// case-policy gate treats that as "the policy is not known" and refuses to
// create an index rather than keying it on a guess.

import {
  casePolicyGeneration,
  publishCasePolicy,
  readCasePolicy,
  type MssqlObjectCaseRule,
} from './casePolicyState';

import type { QueryExecutionError } from '@/types/queryExecution';

import { asString, fetchRows, runFetcherQuery, type RowMap } from './index/engines/queryHelper';

/** A SQL Server collation name distinguishes case when it carries a `_CS`
 *  token or is a binary collation (`_BIN` / `_BIN2`) — e.g.
 *  `Latin1_General_CS_AS`, `Latin1_General_BIN2`. `_CI_` collations (the
 *  default `SQL_Latin1_General_CP1_CI_AS` among them) do not. */
export function collationIsCaseSensitive(collation: string): boolean {
  return /_CS(?:_|$)|_BIN2?(?:_|$)/i.test(collation);
}

/** Whether this connection's instance keys DATABASE names case-sensitively.
 *  `undefined` until the probe has succeeded. */
export function mssqlDatabaseNamesCaseSensitive(connectionId: string): boolean | undefined {
  return readCasePolicy(connectionId).mssqlCaseSensitiveDatabaseNames;
}

/** Record an instance's collation (the probe's own path, and the seam unit
 *  tests seed). */
export function rememberMssqlServerCollation(connectionId: string, collation: string): void {
  if (!collation) return;
  publishCasePolicy(connectionId, casePolicyGeneration(connectionId), {
    mssqlCaseSensitiveDatabaseNames: collationIsCaseSensitive(collation),
  });
}

/**
 * Make sure the connection's server collation is known: one row on the
 * introspection lane, skipped when the answer is already remembered. A failed
 * probe leaves it unknown — the caller decides what to do with that; nothing
 * is guessed here.
 */
export async function probeMssqlServerCollation(connectionId: string, database: string): Promise<void> {
  if (mssqlDatabaseNamesCaseSensitive(connectionId) !== undefined) return;
  const generation = casePolicyGeneration(connectionId);
  try {
    const rows = await fetchRows(
      connectionId,
      database,
      "SELECT CAST(SERVERPROPERTY('Collation') AS nvarchar(128)) AS v",
      'mssql-server-collation',
    );
    const collation = asString(rows[0]?.v);
    if (!collation) return;
    // The connection may have gone away (and the id been reused) while the
    // row was in flight — see `casePolicyState.ts`.
    publishCasePolicy(connectionId, generation, {
      mssqlCaseSensitiveDatabaseNames: collationIsCaseSensitive(collation),
    });
  } catch {
    // Unknown stays unknown.
  }
}

/** Whether `databaseIdentity` (the lifecycle's key for a database on this
 *  connection) keys OBJECT and SCHEMA names case-sensitively. `undefined`
 *  until that database's probe has succeeded.
 *
 *  The lookup requires an OWN property: the key is a database NAME, which is
 *  user data, and a database really can be called `constructor` or `toString`.
 *  A plain property read answered those with the inherited `Object.prototype`
 *  member — not `undefined`, so the caller read "already probed", skipped that
 *  database's probe and froze its keys case-sensitively (a function is truthy).
 *  `casePolicyState.ts` also stores the record prototype-less; this is the
 *  reader's half of the same guard. (`Object.prototype.hasOwnProperty.call`,
 *  not `Object.hasOwn` — the renderer's `lib` is ES2020.) */
export function mssqlObjectNamesCaseSensitive(
  connectionId: string,
  databaseIdentity: string,
): boolean | undefined {
  return mssqlObjectCaseRule(connectionId, databaseIdentity)?.caseSensitive;
}

/** The same answer plus HOW it was learnt — `measured: true` means the server
 *  itself compared `N'a' COLLATE CATALOG_DEFAULT` with `N'A' …`, so
 *  `caseSensitive` IS that comparison's `catalog_cs`. Nothing in production
 *  branches on `measured`; it is what lets a real-server test assert that the
 *  measurement RAN in a database where both probe shapes happen to agree (see
 *  {@link MssqlObjectCaseRule}).
 *
 *  Same own-property guard as the accessor above: the key is a database NAME,
 *  i.e. user data, and a database really can be called `constructor`. */
export function mssqlObjectCaseRule(
  connectionId: string,
  databaseIdentity: string,
): MssqlObjectCaseRule | undefined {
  const byDatabase = readCasePolicy(connectionId).mssqlCaseSensitiveObjectNames;
  if (byDatabase === undefined) return undefined;
  if (!Object.prototype.hasOwnProperty.call(byDatabase, databaseIdentity)) return undefined;
  return byDatabase[databaseIdentity];
}

/** Record one database's collation as its catalog rule — true for every
 *  NON-contained database, which is what the unit tests that seed this seam
 *  describe. Filed as NOT measured: a collation NAME is the data collation,
 *  which is the catalog rule only while the database is non-contained. The
 *  probe itself measures the rule (see {@link DATABASE_CASE_SQL}) rather than
 *  coming through here. */
export function rememberMssqlDatabaseCollation(
  connectionId: string,
  databaseIdentity: string,
  collation: string,
): void {
  if (!collation) return;
  publishCasePolicy(connectionId, casePolicyGeneration(connectionId), {
    mssqlCaseSensitiveObjectNames: {
      [databaseIdentity]: { caseSensitive: collationIsCaseSensitive(collation), measured: false },
    },
  });
}

// REAL-SERVER COVERAGE — both halves run against the docker instance in
// `index-invalidation-ddl.realdb.spec.ts`:
//
//   - "MSSQL case-sensitive database" — a NON-contained `COLLATE
//     Latin1_General_CS_AS` database, where the two shapes agree. It asserts
//     the rule was MEASURED (`catalog_cs = 1`), so the arm can no longer pass
//     with the measured shape removed;
//   - "MSSQL partially contained database" — `CONTAINMENT = PARTIAL COLLATE
//     Latin1_General_CS_AS`, where they DISAGREE: the catalog is the fixed
//     case-insensitive one, so the fallback shape would key object names
//     case-SENSITIVELY and strand `dbo.V` after a `DROP VIEW IF EXISTS dbo.v`
//     that really dropped it. That arm fails if the measured shape goes.
//
// Contained databases need `sp_configure 'contained database authentication',
// 1` (server-level, no restart), which `test-databases/mssql/seed/
// 04_contained_databases.sql` applies on setup and the arm re-applies at run
// time. The option is Developer/Enterprise/Standard only, so the arm probes
// the live server and self-skips where it cannot be turned on — Azure SQL Edge
// (`EngineEdition` 9), the committed arm64 default, is exactly that case.
// Note `DATABASEPROPERTYEX(…, 'CatalogCollation')` answers `NULL` on a boxed
// SQL Server (it is an Azure property) — which is why the probe MEASURES the
// rule instead of reading it.

/** The per-database probe's SQL, in the order it is tried.
 *
 *  The first shape MEASURES the catalog rule instead of inferring it from a
 *  collation name: `CATALOG_DEFAULT` is the COLLATE-clause shortcut for "the
 *  collation metadata is compared in", in contained and non-contained
 *  databases alike, so one comparison answers a contained database's fixed
 *  catalog collation, an Azure `CATALOG_COLLATION` and an ordinary database's
 *  own collation without asking which of the three this is. It also needs no
 *  catalog view: `sys.databases.containment` would tell us about containment
 *  only (and `catalog_collation_type` is Azure-only, so naming either column
 *  fails to compile on a server that lacks it, taking the collation down with
 *  it). `a`/`A` is the case pair on purpose — `i`/`I` differ under a Turkish
 *  case-INsensitive collation.
 *
 *  `Collation` rides along in the same row, and is the second shape on its own:
 *  `CATALOG_DEFAULT` arrived with contained databases in SQL Server 2012 (the
 *  floor the connection dialog documents), and a server that does not know it
 *  rejects the whole statement — so the retry is today's query, unchanged,
 *  which keeps such a server keyed exactly as it was rather than leaving it
 *  with no policy and therefore no index. That retry is reached ONLY from a
 *  rejection the server will repeat (see {@link UNSUPPORTED_SHAPE_SQL_ERRORS}):
 *  this shape answers the DATA collation, which is a DIFFERENT rule in a
 *  contained (or Azure `CATALOG_COLLATION`) database. */
const DATABASE_CASE_SQL = [
  "SELECT CAST(DATABASEPROPERTYEX(DB_NAME(), 'Collation') AS nvarchar(128)) AS v," +
    " CASE WHEN N'a' COLLATE CATALOG_DEFAULT = N'A' COLLATE CATALOG_DEFAULT THEN 0 ELSE 1 END AS catalog_cs",
  "SELECT CAST(DATABASEPROPERTYEX(DB_NAME(), 'Collation') AS nvarchar(128)) AS v",
] as const;

/** `catalog_cs` as the server answered it — `1` = the catalog tells `a` from
 *  `A`. Drivers hand a `CASE … THEN 0 ELSE 1 END` column back as a number, a
 *  numeric string or a boolean; anything else (a `NULL`, or the fallback shape,
 *  which has no such column) means "the rule was not measured". */
function asCatalogCaseSensitive(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value !== 0 : undefined;
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value.trim()) !== 0;
  return undefined;
}

/** The catalog comparison rule one probe row carries, and which shape answered
 *  it: the MEASURED answer when the server gave one, else the database's own
 *  collation — which IS the catalog collation in every non-contained database,
 *  and is all a pre-2012 server can say. `undefined` = the row answered
 *  neither. */
function objectCaseRuleFromRow(row: RowMap | undefined): MssqlObjectCaseRule | undefined {
  const measured = asCatalogCaseSensitive(row?.catalog_cs);
  if (measured !== undefined) return { caseSensitive: measured, measured: true };
  const collation = asString(row?.v);
  return collation ? { caseSensitive: collationIsCaseSensitive(collation), measured: false } : undefined;
}

/** SQL Server error numbers that mean this server CANNOT RUN the measured
 *  shape. All four reject a CONSTANT statement at compile time, so "it failed
 *  once" really does mean "it will fail every time" — which is the only thing
 *  that makes the smaller fallback shape the right next move.
 *
 *  448 was REPRODUCED on the test instance (SQL Server 2025 Developer,
 *  17.0.4075.5); 102, 156 and 40514 are read from their `sys.messages` texts —
 *  40514 cannot be produced on that server at all, being Azure-only. The
 *  distinction matters: only the first is a measurement.
 *
 *    448   `Invalid collation '%.*ls'.` — what a server that does not know
 *          `CATALOG_DEFAULT` answers. `COLLATE` takes a collation NAME, so a
 *          pre-2012 parser reads the token as one and rejects it as unknown;
 *          `SELECT N'a' COLLATE CATALOG_DEFAULT_XYZ` reproduces it verbatim
 *          (`Msg 448, Level 16, State 1` / `Invalid collation
 *          'CATALOG_DEFAULT_XYZ'.`). This is the expected pre-2012 rejection.
 *    102   `Incorrect syntax near '%.*ls'.`
 *    156   `Incorrect syntax near the keyword '%.*ls'.` — the parser-level
 *          rejections, for a grammar that refuses the token before it ever
 *          looks the collation name up.
 *    40514 `'%ls' is not supported in this version of SQL Server.` — the Azure
 *          SQL / Synapse spelling of "this server lacks that feature".
 *
 *  Everything else is TRANSIENT and must NOT fall back: a request timeout (no
 *  server number at all), a deadlock victim (1205), a severed connection, a
 *  cancelled statement, a failed IPC bridge. The fallback shape reads
 *  `DATABASEPROPERTYEX(DB_NAME(), 'Collation')` — the DATA collation, which is
 *  the CATALOG collation only in a NON-contained database — so taking it after
 *  a blip would key a CONTAINED `_CS_` database's object names
 *  case-SENSITIVELY and freeze that wrong answer until reconnect, recreating
 *  the catalog/data bug this probe exists to fix. Unknown costs one refused
 *  index build (`'policy-unknown'`) and the next request probes again; wrong is
 *  not repairable. */
const UNSUPPORTED_SHAPE_SQL_ERRORS: ReadonlySet<number> = new Set([102, 156, 448, 40514]);

/** SSTS renders a server error as `Msg 448, Level 16, State 1, Line 1` on the
 *  FIRST line and fills in no `sqlErrorCode` (only the tedious driver adapter
 *  does), so the number is read off that header when the structured field is
 *  missing — same anchoring rule as `mssqlStsRetryEvidence.ts`, because a
 *  RAISERROR text can spell `Msg …` further down. */
const STS_SERVER_MESSAGE_HEADER = /^\s*Msg (\d+),/;

function mssqlServerErrorNumber(error: QueryExecutionError | undefined): number | undefined {
  if (typeof error?.sqlErrorCode === 'number') return error.sqlErrorCode;
  for (const text of [error?.message, error?.originalMessage]) {
    const match = text ? STS_SERVER_MESSAGE_HEADER.exec(text) : null;
    if (match) return Number(match[1]);
  }
  return undefined;
}

/** What one probe shape settled as: the row it answered (`undefined` for an
 *  empty result set — a query that RAN), a rejection this server will repeat,
 *  or a failure that says nothing about the server. */
type DatabaseCaseAttempt =
  | { readonly outcome: 'row'; readonly row: RowMap | undefined }
  | { readonly outcome: 'unsupported-shape' }
  | { readonly outcome: 'transient-failure' };

/** Run one shape and classify its failure. Goes through `runFetcherQuery`
 *  rather than `fetchRows` — same introspection lane, same cap — because
 *  `fetchRows` throws a string-formatted `Error` that drops `sqlErrorCode` and
 *  un-anchors SSTS's `Msg …` header, and the number is exactly what decides
 *  whether the fallback shape may be asked. (The row cap `fetchRows` guards
 *  cannot bite here: these shapes select one row of two scalars.) */
async function runDatabaseCaseShape(
  connectionId: string,
  database: string,
  sql: string,
): Promise<DatabaseCaseAttempt> {
  try {
    const result = await runFetcherQuery(connectionId, database, sql, 'mssql-database-collation');
    if (result.success) {
      const rows = (result.resultSets?.[0]?.rows ?? []) as readonly RowMap[];
      return { outcome: 'row', row: rows[0] };
    }
    const errorNumber = mssqlServerErrorNumber(result.error);
    return errorNumber !== undefined && UNSUPPORTED_SHAPE_SQL_ERRORS.has(errorNumber)
      ? { outcome: 'unsupported-shape' }
      : { outcome: 'transient-failure' };
  } catch {
    // The IPC bridge itself failed — the server never answered, so nothing was
    // learnt about what it can run.
    return { outcome: 'transient-failure' };
  }
}

/** One round trip, plus the legacy retry ONLY when the server rejected the
 *  first shape as syntax it cannot compile. A query that ran and answered
 *  nothing usable is not a reason to ask again with a strictly smaller one —
 *  and neither is a transient failure, which leaves the policy unknown and
 *  retriable rather than freezing the data collation as the catalog rule (see
 *  {@link UNSUPPORTED_SHAPE_SQL_ERRORS}). */
async function fetchDatabaseCaseRow(
  connectionId: string,
  database: string,
): Promise<RowMap | undefined> {
  const [measuredSql, legacySql] = DATABASE_CASE_SQL;
  const measured = await runDatabaseCaseShape(connectionId, database, measuredSql);
  if (measured.outcome === 'row') return measured.row;
  if (measured.outcome === 'transient-failure') return undefined;
  const legacy = await runDatabaseCaseShape(connectionId, database, legacySql);
  return legacy.outcome === 'row' ? legacy.row : undefined;
}

/**
 * Make sure one database's catalog case rule is known: one row, run IN that
 * database so `DB_NAME()` and `CATALOG_DEFAULT` are that database's, skipped
 * when the answer is already remembered. `database` routes the query (`''` =
 * the login default); `databaseIdentity` is the key the answer is filed under —
 * the same key the lifecycle files that database's index under, so the two
 * cannot drift apart.
 */
export async function probeMssqlDatabaseCollation(
  connectionId: string,
  database: string,
  databaseIdentity: string,
): Promise<void> {
  if (mssqlObjectNamesCaseSensitive(connectionId, databaseIdentity) !== undefined) return;
  const generation = casePolicyGeneration(connectionId);
  const rule = objectCaseRuleFromRow(await fetchDatabaseCaseRow(connectionId, database));
  if (rule === undefined) return;
  // The connection may have gone away (and the id been reused) while the row
  // was in flight — see `casePolicyState.ts`.
  publishCasePolicy(connectionId, generation, {
    mssqlCaseSensitiveObjectNames: { [databaseIdentity]: rule },
  });
}
